import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Addition from './components/Addition';
import Subtraction from './components/Subtraction';
import Multiplication from './components/Multiplication';
import Division from './components/Division';
import Exam from './components/Exam';
import SubtractionExam from './components/SubtractionExam';
import MultiplicationExam from './components/MultiplicationExam';
import DivisionExam from './components/DivisionExam';
import Help from './components/Help';
import './components/Help.css';

function App() {
  // Global metrics state
  const [globalMetrics, setGlobalMetrics] = useState(() => {
    const saved = localStorage.getItem('bellMathsMetrics');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate that papersSaved is a reasonable number
        if (Number.isFinite(parsed.papersSaved) && parsed.papersSaved >= 0 && parsed.papersSaved <= 1000000) {
          return parsed;
        } else {
          console.warn('Invalid papersSaved value detected, resetting metrics');
          return { papersSaved: 0, treesSaved: 0, co2Saved: 0 };
        }
      } catch (error) {
        console.warn('Error parsing metrics from localStorage, resetting');
        return { papersSaved: 0, treesSaved: 0, co2Saved: 0 };
      }
    }
    return { papersSaved: 0, treesSaved: 0, co2Saved: 0 };
  });

  // App state
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [currentMode, setCurrentMode] = useState('practice'); // 'practice' or 'exam'
  const [currentOperation, setCurrentOperation] = useState('addition');
  const [showHelp, setShowHelp] = useState(false);

  // Calculate global metrics
  const calculateGlobalMetrics = () => {
    const papersSaved = Number(globalMetrics.papersSaved) || 0;
    const treesSaved = (papersSaved / 100).toFixed(2); // 1 tree per 100 papers, show 2 decimal places
    const co2Saved = (papersSaved * 0.5).toFixed(2); // 0.5 kg CO2 per paper, show 2 decimal places
    return { papersSaved, treesSaved, co2Saved };
  };

  // Reset corrupted metrics
  const resetMetrics = () => {
    const cleanMetrics = { papersSaved: 0, treesSaved: 0, co2Saved: 0 };
    setGlobalMetrics(cleanMetrics);
    localStorage.setItem('bellMathsMetrics', JSON.stringify(cleanMetrics));
  };

  // Update metrics when components report usage
  const updateMetrics = (newPapersSaved) => {
    // Validate input to prevent NaN values
    const validNewPapers = Number.isFinite(newPapersSaved) && newPapersSaved > 0 ? newPapersSaved : 0;
    const currentPapers = Number(globalMetrics.papersSaved) || 0;
    
    // Check if current papers value is corrupted (non-numeric or extremely large)
    if (!Number.isFinite(currentPapers) || currentPapers > 1000000) {
      console.warn('Detected corrupted metrics, resetting to 0');
      resetMetrics();
      return;
    }
    
    const updatedMetrics = {
      papersSaved: currentPapers + validNewPapers,
      treesSaved: 0, // Will be calculated
      co2Saved: 0 // Will be calculated
    };
    setGlobalMetrics(updatedMetrics);
    localStorage.setItem('bellMathsMetrics', JSON.stringify(updatedMetrics));
  };

  // Reset instructions when operation changes
  const handleOperationChange = (operation) => {
    setCurrentOperation(operation);
    setShowInstructions(true);
    setCurrentMode('learn'); // Set mode to 'learn' when operation changes
    
    // Scroll to the button area (Start Practice, Practice, Exam buttons) after a short delay
    setTimeout(() => {
      const buttonArea = document.querySelector('.start-practice, .mode-toggle');
      if (buttonArea) {
        buttonArea.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  // Function to start the app (called from landing page)
  const startApp = (operation = 'addition', mode = 'learn') => {
    setShowLandingPage(false);
    setCurrentOperation(operation);
    setShowInstructions(true);
    setCurrentMode(mode);
  };

  // Function to toggle help
  const toggleHelp = () => {
    setShowHelp(!showHelp);
  };

  // If showing landing page, render it
  if (showLandingPage) {
    return (
      <Router>
        <div className="app-container">
          <LandingPage onGetStarted={startApp} />
        </div>
      </Router>
    );
  }

  // Main application interface
  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        {/* Metrics Bar - Positioned at the top with same background as header */}
        <div style={{
          backgroundColor: '#667eea',
          color: 'white',
          padding: '8px 20px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '500',
          borderBottom: '1px solid rgba(255,255,255,0.2)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '30px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📄</span>
              <span>Papers Saved: <strong>{calculateGlobalMetrics().papersSaved}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🌳</span>
              <span>Trees Saved: <strong>{calculateGlobalMetrics().treesSaved}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🌍</span>
              <span>CO₂ Saved: <strong>{calculateGlobalMetrics().co2Saved}</strong> kg</span>
            </div>
          </div>
        </div>
        
        {/* Main Header Content - Preserving original layout */}
        <div className="header-content">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%'
          }}>
            {/* Back to Landing Page Button */}
            <div className="logo" style={{ cursor: 'pointer' }} onClick={() => setShowLandingPage(true)}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '15px',
              marginBottom: '10px'
            }}>
              <img 
                src="https://encycdata.com/static/media/logo.f8886f3d8aa8acd1ba80.png" 
                alt="Encycdata Logo" 
                style={{
                  height: '45px',
                  width: 'auto',
                  borderRadius: '6px',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
                  backgroundColor: 'white',
                  padding: '4px',
                  filter: 'brightness(1.1) contrast(1.2)'
                }}
              />
              <h1 style={{ margin: 0 }}>BellMaths</h1>
            </div>
            <div style={{
              textAlign: 'left',
              marginTop: '5px'
            }}>
              <p style={{ 
                margin: '0 0 8px 0', 
                fontSize: '14px',
                opacity: 0.9
              }}>
                Go Green Initiative by <a href="https://www.encycdata.com" target="_blank" rel="noopener noreferrer">Encycdata</a>
              </p>
            </div>
            </div>

            {/* Help Icon */}
            <button 
              onClick={toggleHelp}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                marginTop: '10px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
              }}
            >
              <span style={{ 
                color: 'white', 
                fontSize: '24px',
                fontWeight: 'bold'
              }}>
                ?
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-content">
        {/* Content Area */}
        <div className="content-area">
          {/* Sidebar - Operation-Centric Navigation */}
          <aside className="sidebar">
            <div className="sidebar-content">
              {/* Operation Navigation */}
              <div className="operation-nav">
                <h3 style={{ 
                  marginBottom: '15px', 
                  color: '#667eea', 
                  fontSize: '1.1rem',
                  textAlign: 'center',
                  borderBottom: '2px solid #667eea',
                  paddingBottom: '8px'
                }}>
                  🧮 Math Operations
                </h3>
                
                {/* Elementary School */}
                <div className="category-section">
                  <h4 style={{ 
                    fontSize: '0.9rem', 
                    color: '#495057', 
                    marginBottom: '10px',
                    fontWeight: '600'
                  }}>
                    Elementary School
                  </h4>
                  <ul className="operation-list">
                    <li className={currentOperation === 'addition' ? 'active' : ''}>
                      <button 
                        className="operation-btn" 
                        onClick={() => handleOperationChange('addition')}
                        style={{
                          backgroundColor: currentOperation === 'addition' ? '#667eea' : '#f8f9fa',
                          color: currentOperation === 'addition' ? 'white' : '#495057',
                          border: currentOperation === 'addition' ? 'none' : '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          fontWeight: currentOperation === 'addition' ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ➕ Addition
                      </button>
                    </li>
                    <li className={currentOperation === 'subtraction' ? 'active' : ''}>
                      <button 
                        className="operation-btn" 
                        onClick={() => handleOperationChange('subtraction')}
                        style={{
                          backgroundColor: currentOperation === 'subtraction' ? '#667eea' : '#f8f9fa',
                          color: currentOperation === 'subtraction' ? 'white' : '#495057',
                          border: currentOperation === 'subtraction' ? 'none' : '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          fontWeight: currentOperation === 'subtraction' ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ➖ Subtraction
                      </button>
                    </li>
                    <li className={currentOperation === 'multiplication' ? 'active' : ''}>
                      <button 
                        className="operation-btn" 
                        onClick={() => handleOperationChange('multiplication')}
                        style={{
                          backgroundColor: currentOperation === 'multiplication' ? '#667eea' : '#f8f9fa',
                          color: currentOperation === 'multiplication' ? 'white' : '#495057',
                          border: currentOperation === 'multiplication' ? 'none' : '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          fontWeight: currentOperation === 'multiplication' ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ✖️ Multiplication
                      </button>
                    </li>
                    <li className={currentOperation === 'division' ? 'active' : ''}>
                      <button 
                        className="operation-btn" 
                        onClick={() => handleOperationChange('division')}
                        style={{
                          backgroundColor: currentOperation === 'division' ? '#667eea' : '#f8f9fa',
                          color: currentOperation === 'division' ? 'white' : '#495057',
                          border: currentOperation === 'division' ? 'none' : '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          fontWeight: currentOperation === 'division' ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ➗ Division
                      </button>
                    </li>
                  </ul>
                </div>

                {/* Middle School */}
                <div className="category-section">
                  <h4 style={{ 
                    fontSize: '0.9rem', 
                    color: '#495057', 
                    marginBottom: '10px',
                    fontWeight: '600'
                  }}>
                    Middle School
                  </h4>
                  <ul className="operation-list">
                    <li>
                      <button 
                        className="operation-btn" 
                        disabled
                        style={{
                          backgroundColor: '#f8f9fa',
                          color: '#adb5bd',
                          border: '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'not-allowed',
                          opacity: 0.6
                        }}
                      >
                        📐 Geometry (Coming Soon)
                      </button>
                    </li>
                    <li>
                      <button 
                        className="operation-btn" 
                        disabled
                        style={{
                          backgroundColor: '#f8f9fa',
                          color: '#adb5bd',
                          border: '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'not-allowed',
                          opacity: 0.6
                        }}
                      >
                        📊 Statistics (Coming Soon)
                      </button>
                    </li>
                  </ul>
                </div>

                {/* High School */}
                <div className="category-section">
                  <h4 style={{ 
                    fontSize: '0.9rem', 
                    color: '#495057', 
                    marginBottom: '10px',
                    fontWeight: '600'
                  }}>
                    High School
                  </h4>
                  <ul className="operation-list">
                    <li>
                      <button 
                        className="operation-btn" 
                        disabled
                        style={{
                          backgroundColor: '#f8f9fa',
                          color: '#adb5bd',
                          border: '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'not-allowed',
                          opacity: 0.6
                        }}
                      >
                        🧮 Algebra (Coming Soon)
                      </button>
                    </li>
                    <li>
                      <button 
                        className="operation-btn" 
                        disabled
                        style={{
                          backgroundColor: '#f8f9fa',
                          color: '#adb5bd',
                          border: '1px solid #e9ecef',
                          padding: '12px 15px',
                          borderRadius: '6px',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'not-allowed',
                          opacity: 0.6
                        }}
                      >
                        📈 Calculus (Coming Soon)
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Practice Area */}
          <main className="practice-area">
            {/* Operation Header with Mode Toggle */}
            <div className="operation-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              padding: '15px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div className="operation-info">
                <h2 style={{ 
                  margin: 0, 
                  color: '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {currentOperation === 'addition' && '➕ Addition'}
                  {currentOperation === 'subtraction' && '➖ Subtraction'}
                  {currentOperation === 'multiplication' && '✖️ Multiplication'}
                  {currentOperation === 'division' && '➗ Division'}
                </h2>
                <p style={{ 
                  margin: '5px 0 0 0', 
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  {currentOperation === 'addition' && 'Master basic addition with step-by-step solutions'}
                  {currentOperation === 'subtraction' && 'Master basic subtraction with step-by-step solutions'}
                  {currentOperation === 'multiplication' && 'Master long multiplication with step-by-step solutions'}
                  {currentOperation === 'division' && 'Master long division with step-by-step solutions'}
                </p>
              </div>
              
              <div className="mode-toggle" style={{
                display: 'flex',
                gap: '10px'
              }}>
                <button 
                  className={`btn ${currentMode === 'learn' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setCurrentMode('learn')}
                  style={{ 
                    padding: '8px 16px', 
                    fontSize: '14px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📚 Learn
                </button>
                <button 
                  className={`btn ${currentMode === 'practice' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setCurrentMode('practice')}
                  style={{ 
                    padding: '8px 16px', 
                    fontSize: '14px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📝 Practice
                </button>
                <button 
                  className={`btn ${currentMode === 'exam' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setCurrentMode('exam')}
                  style={{ 
                    padding: '8px 16px', 
                    fontSize: '14px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  🎯 Exam
                </button>
              </div>
            </div>

            {/* Operation Content */}
                        {currentMode === 'learn' ? (
                <div className="instructions-page">
                  <div className="paper-container">
                    <div className="addition-specific">
                      <h3>{currentOperation === 'addition' ? 'Addition' : currentOperation === 'subtraction' ? 'Subtraction' : currentOperation === 'multiplication' ? 'Multiplication' : 'Division'}-Specific Instructions</h3>
                      <div className="addition-steps">
                        {currentOperation === 'addition' ? (
                          <>
                            <div className="instruction-step">
                              <strong>Understanding Addition:</strong> Addition combines two or more numbers to find their total sum.
                            </div>
                            <div className="instruction-step">
                              <strong>Carry-Over:</strong> When the sum of digits in a column exceeds 9, you carry the extra value to the next column.
                            </div>
                            <div className="instruction-step">
                              <strong>Working from Right to Left:</strong> Start adding from the ones place (rightmost digit) and work towards the left.
                            </div>
                            <div className="instruction-step">
                              <strong>Using Carry Fields:</strong> Enter carry values in the orange boxes above the numbers when needed.
                            </div>
                            <div className="instruction-step">
                              <strong>Virtual Keyboard:</strong> Use the on-screen keyboard for easier input on touch devices.
                            </div>
                          </>
                        ) : currentOperation === 'subtraction' ? (
                          <>
                            <div className="instruction-step">
                              <strong>Understanding Subtraction:</strong> Subtraction finds the difference between two numbers.
                            </div>
                            <div className="instruction-step">
                              <strong>Borrowing:</strong> When the top digit is smaller than the bottom digit, you borrow from the next column.
                            </div>
                            <div className="instruction-step">
                              <strong>Working from Right to Left:</strong> Start subtracting from the ones place (rightmost digit) and work towards the left.
                            </div>
                            <div className="instruction-step">
                              <strong>Using Borrow Fields:</strong> Enter borrow values in the orange boxes and remaining values in the green boxes when needed.
                            </div>
                            <div className="instruction-step">
                              <strong>Virtual Keyboard:</strong> Use the on-screen keyboard for easier input on touch devices.
                            </div>
                          </>
                        ) : currentOperation === 'multiplication' ? (
                          <>
                            <div className="instruction-step">
                              <strong>Understanding Multiplication:</strong> Multiplication is repeated addition, finding the product of two numbers.
                            </div>
                            <div className="instruction-step">
                              <strong>Long Multiplication:</strong> Multiply each digit of the second number by the first number, then add all partial products.
                            </div>
                            <div className="instruction-step">
                              <strong>Working from Right to Left:</strong> Start with the rightmost digit of the multiplier and work towards the left.
                            </div>
                            <div className="instruction-step">
                              <strong>Using Partial Product Fields:</strong> Enter partial products in the white boxes and carry values in the orange boxes when needed.
                            </div>
                            <div className="instruction-step">
                              <strong>Virtual Keyboard:</strong> Use the on-screen keyboard for easier input on touch devices.
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="instruction-step">
                              <strong>Understanding Division:</strong> Division is the process of finding how many times one number fits into another.
                            </div>
                            <div className="instruction-step">
                              <strong>Long Division:</strong> Divide the dividend by the divisor, finding the quotient digit by digit.
                            </div>
                            <div className="instruction-step">
                              <strong>Working from Left to Right:</strong> Start with the leftmost digit of the dividend and work towards the right.
                            </div>
                            <div className="instruction-step">
                              <strong>Using Working Fields:</strong> Enter quotient digits, working divisors, partial products, and remainders in their respective boxes.
                            </div>
                            <div className="instruction-step">
                              <strong>Virtual Keyboard:</strong> Use the on-screen keyboard for easier input on touch devices.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Learning Videos Section */}
                    <div className="learning-videos" style={{ marginTop: '30px' }}>
                      <h3 style={{ color: '#667eea', marginBottom: '20px', textAlign: 'center' }}>🎥 Learning Videos</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                        {currentOperation === 'addition' ? (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Addition</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <iframe 
                                  width="100%" 
                                  height="200" 
                              src="https://www.youtube.com/embed/AuX7nPBqDts" 
                                  title="Basic Addition"
                              frameBorder="0" 
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn the fundamentals of addition with carrying</p>
                          </div>
                        </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Addition with Regrouping</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Addition with Regrouping"
                              frameBorder="0" 
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master addition with multiple digits and carrying</p>
                          </div>
                        </div>
                          </>
                        ) : currentOperation === 'subtraction' ? (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Subtraction</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Basic Subtraction"
                              frameBorder="0" 
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn subtraction fundamentals and borrowing</p>
                          </div>
                        </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Subtraction with Borrowing</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Subtraction with Borrowing"
                              frameBorder="0" 
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master multi-digit subtraction with borrowing</p>
                          </div>
                        </div>
                          </>
                        ) : currentOperation === 'multiplication' ? (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Multiplication</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Basic Multiplication"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn multiplication tables and basic concepts</p>
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Long Multiplication</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Long Multiplication"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master multi-digit multiplication step by step</p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Division</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Basic Division"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn division fundamentals and remainders</p>
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Long Division</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Long Division"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master long division with step-by-step process</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Learning Videos Section */}
                    <div className="learning-videos" style={{ marginTop: '30px' }}>
                      <h3 style={{ color: '#667eea', marginBottom: '20px', textAlign: 'center' }}>🎥 Learning Videos</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                        {currentOperation === 'addition' ? (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Addition</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/AuX7nPBqDts" 
                                  title="Basic Addition"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn the fundamentals of addition with carrying</p>
                  </div>
                </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Addition with Regrouping</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Addition with Regrouping"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master addition with multiple digits and carrying</p>
                              </div>
                            </div>
                          </>
                        ) : currentOperation === 'subtraction' ? (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Subtraction</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Basic Subtraction"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn subtraction fundamentals and borrowing</p>
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Subtraction with Borrowing</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Subtraction with Borrowing"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master multi-digit subtraction with borrowing</p>
                              </div>
                            </div>
                          </>
                        ) : currentOperation === 'multiplication' ? (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Multiplication</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Basic Multiplication"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn multiplication tables and basic concepts</p>
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Long Multiplication</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Long Multiplication"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master multi-digit multiplication step by step</p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Basic Division</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Basic Division"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Learn division fundamentals and remainders</p>
                              </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <h4 style={{ color: '#333', marginBottom: '15px' }}>Long Division</h4>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <iframe 
                                  width="100%" 
                                  height="200" 
                                  src="https://www.youtube.com/embed/8hz0fAWVfqc" 
                                  title="Long Division"
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  allowFullScreen
                                  style={{ borderRadius: '8px', marginBottom: '10px' }}
                                ></iframe>
                                <p style={{ color: '#666', fontSize: '14px' }}>Master long division with step-by-step process</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentMode === 'practice' ? (
                <div className="paper-container">
                  {currentOperation === 'addition' ? (
                    <Addition key="addition" updateMetrics={updateMetrics} />
                  ) : currentOperation === 'subtraction' ? (
                    <Subtraction key="subtraction" updateMetrics={updateMetrics} />
                  ) : currentOperation === 'multiplication' ? (
                    <Multiplication key="multiplication" updateMetrics={updateMetrics} />
                  ) : (
                    <Division key="division" updateMetrics={updateMetrics} />
                  )}
                </div>
            ) : (
              <div className="paper-container">
                  {currentOperation === 'addition' ? (
                    <Exam key="addition-exam" updateMetrics={updateMetrics} />
                  ) : currentOperation === 'subtraction' ? (
                    <SubtractionExam key="subtraction-exam" updateMetrics={updateMetrics} />
                ) : currentOperation === 'multiplication' ? (
                    <MultiplicationExam key="multiplication-exam" updateMetrics={updateMetrics} />
                  ) : currentOperation === 'division' ? (
                    <DivisionExam key="division-exam" updateMetrics={updateMetrics} />
                ) : (
                    <Exam key="default-exam" updateMetrics={updateMetrics} />
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>&copy; 2024 BellMaths - Go Green Initiative by <a href="https://www.encycdata.com" target="_blank" rel="noopener noreferrer">Encycdata</a></p>
          <p>Eliminating Paper-Pencil Work by Enabling Digital Transformation 🌱</p>
        </div>
      </footer>

      {/* Help Modal */}
      <Help 
        isOpen={showHelp} 
        onClose={() => setShowHelp(false)} 
        currentOperation={currentOperation} 
      />
    </div>
  );
}

export default App; 